const COPY = {
  en: { title: 'Development', empty: 'No development links', branch: 'Branch', commit: 'Commit', pr: 'Pull request',
    kind: 'Kind', url: 'HTTPS URL', label: 'Label (optional)', state: 'State (optional)', add: 'Add link', remove: 'Remove link',
    failed: 'Could not save development link. Check the HTTPS URL and try again.',
    removeFailed: 'Could not remove development link. Try again.',
    states: { open: 'Open', merged: 'Merged', closed: 'Closed', active: 'Active' } },
  uk: { title: 'Розробка', empty: 'Посилань на розробку немає', branch: 'Гілка', commit: 'Коміт', pr: 'Запит на злиття',
    kind: 'Тип', url: 'HTTPS URL', label: 'Назва (необов’язково)', state: 'Стан (необов’язково)', add: 'Додати посилання', remove: 'Видалити посилання',
    failed: 'Не вдалося зберегти посилання. Перевірте HTTPS URL і спробуйте ще раз.',
    removeFailed: 'Не вдалося видалити посилання. Спробуйте ще раз.',
    states: { open: 'Відкрито', merged: 'Злито', closed: 'Закрито', active: 'Активна' } },
};
const KINDS = ['branch', 'commit', 'pr'];
const PATHS = {
  branch: '<circle cx="6" cy="5" r="3"/><circle cx="18" cy="5" r="3"/><circle cx="6" cy="19" r="3"/><path d="M6 8v8M18 8a9 9 0 0 1-9 9H6"/>',
  commit: '<circle cx="12" cy="12" r="5"/><path d="M2 12h5m10 0h5"/>',
  pr: '<circle cx="6" cy="5" r="3"/><circle cx="6" cy="19" r="3"/><circle cx="18" cy="19" r="3"/><path d="M6 8v8M18 16V9a4 4 0 0 0-4-4h-2m3-3-3 3 3 3"/>',
};
const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const copyFor = language => COPY[language] || COPY.en;

function safeUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

export function renderDevLinkRows(links, language, canWrite) {
  const copy = copyFor(language);
  return links.map(link => {
    const url = safeUrl(link.url);
    if (!url || !KINDS.includes(link.kind)) return '';
    return `<div class="task-dev-link-row">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">${PATHS[link.kind]}</svg>
      <a href="${esc(url)}" target="_blank" rel="noopener noreferrer"><span class="task-dev-link-kind">${copy[link.kind]}</span> ${esc(link.label || link.external_id || url)}${link.repo ? `<small>${esc(link.repo)}</small>` : ''}</a>
      ${link.state ? `<span class="badge badge-sm">${esc(Object.hasOwn(copy.states, link.state) ? copy.states[link.state] : link.state)}</span>` : ''}
      ${canWrite ? `<button type="button" class="btn btn-ghost btn-sm" data-dev-link-delete="${Number(link.id)}" aria-label="${copy.remove}" title="${copy.remove}">&times;</button>` : ''}
    </div>`;
  }).join('') || `<span class="text-muted">${copy.empty}</span>`;
}

export function renderDevLinks(links = [], language = 'en', canWrite = false) {
  const copy = copyFor(language);
  return `<section class="task-detail-field task-detail-section task-dev-links" aria-label="${copy.title}">
    <span class="task-detail-field-label">${copy.title}</span>
    <div class="task-dev-link-list">${renderDevLinkRows(links, language, canWrite)}</div>
    ${canWrite ? `<form class="task-dev-link-form">
      <label>${copy.kind}<select class="form-select form-select-sm" name="kind">${KINDS.map(kind => `<option value="${kind}">${copy[kind]}</option>`).join('')}</select></label>
      <label class="task-dev-link-url">${copy.url}<input class="form-input form-input-sm" type="url" name="url" required pattern="https://.*" maxlength="2048" placeholder="https://…"></label>
      <label>${copy.label}<input class="form-input form-input-sm" name="label" maxlength="255"></label>
      <label>${copy.state}<input class="form-input form-input-sm" name="state" maxlength="32"></label>
      <button type="submit" class="btn btn-ghost btn-sm">${copy.add}</button>
    </form>` : ''}
    <p class="task-dev-link-error text-danger" role="alert" hidden></p>
  </section>`;
}

// Bind once to the owning drawer/page. Repaints replace the section but leave these
// delegated listeners intact. Pending replies update their original task only.
export function bindDevLinks(root, getState, api) {
  const pending = new WeakSet();
  async function mutate(event, linkId) {
    const owner = getState();
    if (!owner || pending.has(owner)) return;
    const section = event.target.closest('.task-dev-links');
    if (!section) return;
    event.preventDefault();
    const form = section.querySelector('form');
    if (!form) return; // viewers have no write controls
    const deleting = linkId != null;
    if (!deleting && !form.reportValidity()) return;
    const body = deleting ? null : Object.fromEntries(new FormData(form));
    const path = `/projects/${owner.projectId}/tasks/${encodeURIComponent(owner.taskKey)}/dev-links`;
    const error = section.querySelector('.task-dev-link-error');
    error.hidden = true;
    pending.add(owner);
    const controls = [...section.querySelectorAll('input, select, button')];
    controls.forEach(control => { control.disabled = true; });
    try {
      const result = deleting ? await api.delete(`${path}/${linkId}`) : await api.post(path, body);
      let links = owner.task.dev_links || [];
      links = deleting ? links.filter(link => Number(link.id) !== Number(linkId))
        : [...links.filter(link => link.id !== result.dev_link.id), result.dev_link].sort((a, b) => a.id - b.id);
      owner.task.dev_links = links;
      if (getState() === owner) {
        const live = root.querySelector('.task-dev-links');
        if (live) live.querySelector('.task-dev-link-list').innerHTML = renderDevLinkRows(links, owner.project?.language, true);
        if (!deleting) form.reset();
      }
    } catch {
      if (getState() === owner) {
        const liveError = root.querySelector('.task-dev-link-error');
        if (liveError) { liveError.textContent = copyFor(owner.project?.language)[deleting ? 'removeFailed' : 'failed']; liveError.hidden = false; }
      }
    } finally {
      pending.delete(owner);
      controls.forEach(control => { control.disabled = false; });
    }
  }
  const onSubmit = event => {
    if (!event.target.matches('.task-dev-link-form')) return;
    event.preventDefault();
    void mutate(event);
  };
  const onClick = event => {
    const button = event.target.closest('[data-dev-link-delete]');
    if (button && !button.disabled) void mutate(event, button.dataset.devLinkDelete);
  };
  root.addEventListener('submit', onSubmit);
  root.addEventListener('click', onClick);
  return () => { root.removeEventListener('submit', onSubmit); root.removeEventListener('click', onClick); };
}
